import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Save as SaveIcon,
  Science as TrialIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { useUserData } from '../../hooks/useUserData';
import { enrollPatientInTrial } from '../../services/tumorBoardService';
import { PageHeader, ContentCard } from './oncologyStyles';

const TrialEnrollment = ({ trial, patientId: propPatientId, onComplete, onCancel, embedded = false }) => {
  const { patientId: urlPatientId } = useParams();
  const navigate = useNavigate();
  const patientId = propPatientId || urlPatientId;
  const { defaultFacility } = useUserData();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [screeningResults, setScreeningResults] = useState({});
  const [consentDate, setConsentDate] = useState('');
  const [notes, setNotes] = useState('');

  const steps = ['Eligibility Screening', 'Informed Consent', 'Enrollment'];

  const handleScreeningChange = (criterion, met) => {
    setScreeningResults(prev => ({
      ...prev,
      [criterion]: met
    }));
  };

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleEnroll = async () => {
    try {
      setLoading(true);
      setError(null);

      const enrollmentData = {
        patientId,
        facilityId: defaultFacility.id,
        screeningDate: new Date().toISOString(),
        screeningResults: Object.entries(screeningResults).map(([criterion, met]) => ({
          criterion,
          met
        })),
        consentDate,
        enrollmentStatus: 'Enrolled'
      };

      await enrollPatientInTrial(trial.id, enrollmentData);

      if (onComplete) {
        onComplete();
      }
    } catch (err) {
      console.error('Error enrolling patient:', err);
      setError(err.message || 'Failed to enroll patient');
    } finally {
      setLoading(false);
    }
  };

  const allInclusionMet = trial?.eligibilityCriteria?.inclusion?.every(
    criterion => screeningResults[criterion] === true
  );

  const noExclusionMet = trial?.eligibilityCriteria?.exclusion?.every(
    criterion => screeningResults[criterion] === false || screeningResults[criterion] === undefined
  );

  const isEligible = allInclusionMet && noExclusionMet;

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Inclusion Criteria
            </Typography>
            <List dense>
              {trial?.eligibilityCriteria?.inclusion?.map((criterion, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <Checkbox
                      checked={screeningResults[criterion] === true}
                      onChange={(e) => handleScreeningChange(criterion, e.target.checked)}
                      color="success"
                    />
                  </ListItemIcon>
                  <ListItemText primary={criterion} />
                  {screeningResults[criterion] === true && (
                    <Chip label="Met" size="small" color="success" />
                  )}
                </ListItem>
              ))}
            </List>

            {trial?.eligibilityCriteria?.exclusion?.length > 0 && (
              <>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                  Exclusion Criteria (should NOT be present)
                </Typography>
                <List dense>
                  {trial?.eligibilityCriteria?.exclusion?.map((criterion, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Checkbox
                          checked={screeningResults[criterion] === true}
                          onChange={(e) => handleScreeningChange(criterion, e.target.checked)}
                          color="error"
                        />
                      </ListItemIcon>
                      <ListItemText primary={criterion} />
                      {screeningResults[criterion] === true && (
                        <Chip label="Present - EXCLUDES" size="small" color="error" />
                      )}
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="subtitle2">Eligibility Status</Typography>
                {isEligible ? (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Patient appears eligible for this trial based on screening criteria.
                  </Alert>
                ) : (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Please complete all eligibility screening. Patient must meet all inclusion criteria
                    and none of the exclusion criteria.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="body1" gutterBottom>
              Confirm that informed consent has been obtained from the patient.
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Consent Date"
                  value={consentDate ? new Date(consentDate) : null}
                  onChange={(date) => setConsentDate(date ? date.toISOString().split('T')[0] : '')}
                  slotProps={{
                    textField: { fullWidth: true, size: 'small', required: true }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox required />}
                  label="I confirm that informed consent has been obtained and documented"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox required />}
                  label="Patient has received a copy of the consent form"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox />}
                  label="Patient has been given opportunity to ask questions"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Enrollment Summary</Typography>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold">{trial?.title}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {trial?.nctNumber} • {trial?.phase}
                </Typography>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Screening Status</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isEligible ? (
                        <>
                          <CheckIcon color="success" />
                          <Typography variant="body2" color="success.main">Eligible</Typography>
                        </>
                      ) : (
                        <>
                          <CancelIcon color="error" />
                          <Typography variant="body2" color="error.main">Not Eligible</Typography>
                        </>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Consent Date</Typography>
                    <Typography variant="body2">{consentDate || 'Not provided'}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Enrollment Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes about this enrollment..."
              sx={{ mt: 2 }}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  // Form content that can be reused in different layouts
  const formContent = (
    <>
      {trial && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {trial.title} ({trial.nctNumber})
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stepper activeStep={activeStep} sx={{ my: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ minHeight: 300, mb: 3 }}>
        {renderStepContent(activeStep)}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          startIcon={<BackIcon />}
        >
          Back
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onCancel && (
            <Button variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
          )}
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleEnroll}
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={loading || !isEligible || !consentDate}
            >
              {loading ? 'Enrolling...' : 'Complete Enrollment'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<NextIcon />}
              disabled={activeStep === 0 && !isEligible}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    </>
  );

  // Show message if no trial or patient selected (standalone mode)
  if ((!trial || !patientId) && !embedded) {
    return (
      <Box>
        <PageHeader
          title="Clinical Trial Enrollment"
          subtitle="Enroll patients in clinical trials"
          icon={<TrialIcon />}
          backTo="/oncology/clinical-trials"
        />

        <ContentCard>
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              {!patientId 
                ? 'Please select a patient to enroll in a clinical trial.'
                : 'No trial selected for enrollment.'}
            </Alert>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              To enroll a patient in a clinical trial, first search for available trials and select one for enrollment.
            </Typography>
            <Button variant="outlined" onClick={() => navigate('/oncology/clinical-trials/search')}>
              Search Clinical Trials
            </Button>
          </CardContent>
        </ContentCard>
      </Box>
    );
  }

  // Embedded mode (used within patient context)
  if (embedded) {
    return (
      <ContentCard>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <IconButton onClick={() => onCancel ? onCancel() : navigate(-1)} size="small">
              <ArrowBackIcon />
            </IconButton>
            <TrialIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Clinical Trial Enrollment
            </Typography>
          </Box>

          {formContent}
        </CardContent>
      </ContentCard>
    );
  }

  // Standalone page with patient and trial
  return (
    <Box>
      <PageHeader
        title="Clinical Trial Enrollment"
        subtitle="Enroll patients in clinical trials"
        icon={<TrialIcon />}
        backTo="/oncology/clinical-trials"
      />

      <ContentCard>
        <CardContent>
          {formContent}
        </CardContent>
      </ContentCard>
    </Box>
  );
};

export default TrialEnrollment;

